import { EpubCFI } from "epubjs";
import AnnotationApiClient from "./AnnotationApiClient";
import Bookmark from "./Bookmark";
import Note from "./Note";
import AutoSortingArray from "../AutoSortingArray";
import { DEFAULT_HIGHLIGHT_COLOR } from "./highlightColors";

export default class Annotator {
  /**
   * Creates a new Annotator object.
   * @param {number} bookId Bookshare title instance ID
   * @param {Storage} [storageProvider] Storage-shaped object
   * @param {AnnotationApiClient} [apiClient] API client that provides bookmark/note access
   */
  constructor(bookId, storageProvider, apiClient) {
    /** @type {number} */
    this.bookId = bookId;

    /** @type {AutoSortingArray} */
    this.bookmarks = new AutoSortingArray((a, b) =>
      this.cfiComparator.compare(a.location, b.location),
    );

    /** @type {AutoSortingArray} */
    this.notes = new AutoSortingArray((a, b) => {
      // Check the start points of both CFIs first.
      const startComparison = this.cfiComparator.compare(
        a.location,
        b.location,
      );
      if (startComparison !== 0) {
        return startComparison;
      } else {
        // If both CFIs have the same start point, compare their end points.
        const cfiA = new EpubCFI(a.location);
        const cfiB = new EpubCFI(b.location);
        cfiA.collapse();
        cfiB.collapse();
        return this.cfiComparator.compare(cfiA, cfiB);
      }
    });

    /** @type {EpubCFI} */
    this.cfiComparator = new EpubCFI();

    this.storageProvider = storageProvider || null;

    this.apiClient = apiClient || null;
  }

  /** BOOKMARKS */

  /** Internal functions for Bookmark saves */
  _bookmarkSave(b) {
    return this.apiClient
      ?.saveBookmark(b)
      .then(() => {
        b.synced = true;
        this.storageProvider?.storeBookmarks(
          this.bookId,
          this.listAllBookmarks(),
        );
      })
      .catch((e) => {
        console.log("Save failed.");
        console.log(e);
      });
  }

  _bookmarkUpdate = (b) => {
    // No update API so we're doing this via chained delete/save
    return this.apiClient
      ?.deleteBookmark(b)
      .then(() => this.apiClient?.saveBookmark(b))
      .then(() => {
        b.synced = true;
        this.storageProvider?.storeBookmarks(
          this.bookId,
          this.listAllBookmarks(),
        );
      })
      .catch((e) => {
        console.log("Update failed.");
        console.log(e);
      });
  };

  _bookmarkDelete(b) {
    return this.apiClient
      ?.deleteBookmark(b)
      .then(() => {
        this.bookmarks.remove((x) => x.location === b.location);
        this.storageProvider?.storeBookmarks(
          this.bookId,
          this.listAllBookmarks(),
        );
      })
      .catch((e) => {
        console.log("Delete failed.");
        console.log(e);
      });
  }

  /**
   * Create a new bookmark with the given location and text. If
   * a bookmark already exists with the given location, replace it
   * with the new one.
   * @param {string} location Location as a CFI string
   * @param {string} text Text snippet from the bookmarked location
   * @param {object} locator Optional Readium Locator object
   * @returns {Bookmark} the created Bookmark
   */
  addBookmark(location, text, locator) {
    const b = new Bookmark(this.bookId, location, text, locator);
    // Pop any existing bookmark at that location
    this.bookmarks.remove((b) => b.location === location);
    // Add in the new one
    this.bookmarks.add(b);
    this.storageProvider?.storeBookmarks(this.bookId, this.listAllBookmarks());
    this._bookmarkSave(b);
    return b;
  }

  /**
   * Removes the bookmark at the given location.
   * @param {string} location CFI of bookmark to delete
   * @returns {Bookmark} The deleted bookmark, or null if no bookmark was found for the location.
   */
  removeBookmark(location) {
    const b = this.bookmarks.items.find((b) => b.location === location);
    if (b) {
      // No actual delete just yet, mark as deleted and notify the server.
      b.shouldDelete = true;
      this.storageProvider?.storeBookmarks(
        this.bookId,
        this.listAllBookmarks(),
      );
      this._bookmarkDelete(b);

      // This returns immediately so the caller knows that the delete was
      // processed. listActiveBookmarks() works off this object's internal
      // state so it already reflects this bookmark's removal.
      return b;
    } else {
      return null;
    }
  }

  /**
   * Returns Bookmarks that are not marked for deletion.
   * @returns {Bookmark[]} Array of bookmarks
   */
  listActiveBookmarks() {
    return this.bookmarks.items.filter((b) => !b.shouldDelete);
  }

  /**
   * Returns all Bookmarks, even those marked for deletion
   * @returns {Bookmark[]} Array of bookmarks
   */
  listAllBookmarks() {
    return this.bookmarks.items;
  }

  /**
   * @returns {Promise<Bookmark[]>}
   */
  initializeBookmarks() {
    // clear any existing bookmarks
    this.bookmarks.clear();

    // Load local bookmarks
    const localBookmarks = this.loadBookmarksFromStorage();

    // Load from server
    if (this.apiClient) {
      return this.apiClient
        ?.getBookmarks(this.bookId)
        .then((remoteBookmarks) => {
          this.reconcileBookmarks(localBookmarks, remoteBookmarks);
          return this.listActiveBookmarks();
        })
        .catch((e) => {
          console.log(
            "Bookmark retrieval failed, falling back to stored bookmarks.",
          );
          console.log(e);
          this.bookmarks.add(...localBookmarks);
          return this.listActiveBookmarks();
        });
    } else {
      this.bookmarks.add(...localBookmarks);
      return Promise.resolve(this.listActiveBookmarks());
    }
  }

  /**
   * @returns {Bookmark[]}
   */
  loadBookmarksFromStorage() {
    if (this.storageProvider) {
      return this.storageProvider.loadBookmarks(this.bookId);
    } else {
      return [];
    }
  }

  /**
   * Reconciles local and server bookmarks and updates the internal bookmark property.
   * Side-effects galore here.
   * @param {Bookmark[]} local
   * @param {Bookmark[]} remote
   */
  reconcileBookmarks(local, remote) {
    const result = reconcileAnnotations(
      local,
      remote,
      (b) => b.dateCreated,
      (b) => this._bookmarkSave(b),
      (b) => this._bookmarkUpdate(b),
      (b) => this._bookmarkDelete(b),
    );

    this.bookmarks.clear();
    this.bookmarks.add(...result.values());
    this.storageProvider?.storeBookmarks(this.bookId, this.listAllBookmarks());
  }

  /** NOTES */

  /** Internal functions for Note saves */
  _noteSave(n) {
    return this.apiClient
      ?.saveNote(n)
      .then(() => {
        n.synced = true;
        this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
      })
      .catch((e) => {
        console.log("Save failed.");
        console.log(e);
      });
  }

  _noteUpdate = (n) => {
    return this.apiClient
      ?.editNote(n)
      .then(() => {
        n.synced = true;
        this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
      })
      .catch((e) => {
        console.log("Update failed.");
        console.log(e);
      });
  };

  _noteDelete(n) {
    return this.apiClient
      ?.deleteNote(n)
      .then(() => {
        this.notes.remove((x) => x.location === n.location);
        this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
      })
      .catch((e) => {
        console.log("Delete failed.");
        console.log(e);
      });
  }

  /**
   * Create a new note with the given attributes. If a note already exists with
   * the given CFI location points, the new one replaces it.
   * @param {string} location Note location expressed as a CFI range string
   * @param {string} text     Complete text (not an excerpt) of the noted range, with
   *                          whitespace sequences collapsed.
   * @param {object} color    Color of note, represented as RGB hex code (e.g. '#CC3399')
   * @param {object} note     Optional user-entered text notes
   * @param {object} locator  Optional Readium Locator object
   * @returns {Note} The created note.
   */
  addNote(location, text, color, note, locator) {
    const n = new Note(this.bookId, location, text, color, note, locator);
    // Pop any existing note at that location
    this.notes.remove((n) => n.location === location);
    this.notes.add(n);
    this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
    this._noteSave(n);
    return n;
  }

  /**
   * Removes the note at the given CFI range
   * @param {string} location CFI of note to delete
   * @returns {Note} The deleted note, or null if no note was found for the CFI range.
   */
  removeNote(location) {
    const n = this.notes.items.find((n) => n.location === location);
    if (n) {
      n.shouldDelete = true;
      this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
      this._noteDelete(n);
      return n;
    } else {
      return null;
    }
  }

  /**
   * Updates the note at the given CFI range
   * @param {string} location CFI of note to update
   * @param {string} annotationText Updated text of the note
   * @param {string} [color] Updated color of the note, represented as RGB hex code. Unchanged when omitted.
   * @returns {Note} The updated note, or null if no note was found for the CFI range.
   */
  editNote(location, annotationText, color) {
    const n = this.notes.items.find((n) => n.location === location);
    if (n) {
      n.note = annotationText;
      n.color = color || n.color || DEFAULT_HIGHLIGHT_COLOR;
      this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
      this._noteUpdate(n);
      return n;
    } else {
      return null;
    }
  }

  /**
   * Returns Notes that are not marked for deletion.
   * @returns {Note[]} Array of notes
   */
  listActiveNotes() {
    return this.notes.items.filter((n) => !n.shouldDelete);
  }

  /**
   * Returns all Notes, even those marked for deletion
   * @returns {Note[]} Array of notes
   */
  listAllNotes() {
    return this.notes.items;
  }

  /**
   * @returns {Promise<Note[]>}
   */
  initializeNotes() {
    // Clear any existing notes
    this.notes.clear();

    // Load local notes
    const localNotes = this.loadNotesFromStorage();

    // Load from server
    if (this.apiClient) {
      return this.apiClient
        ?.getNotes(this.bookId)
        .then((remoteNotes) => {
          this.reconcileNotes(localNotes, remoteNotes);
          return this.listActiveNotes();
        })
        .catch((e) => {
          console.log("Notes retrieval failed, falling back to stored notes.");
          console.log(e);
          this.notes.add(...localNotes);
          return this.listActiveNotes();
        });
    } else {
      this.notes.add(...localNotes);
      return Promise.resolve(this.listActiveNotes());
    }
  }

  /**
   * @returns {Note[]}
   */
  loadNotesFromStorage() {
    if (this.storageProvider) {
      return this.storageProvider.loadNotes(this.bookId);
    } else {
      return [];
    }
  }

  /**
   * Reconciles local and server notes and updates the internal notes property.
   * Side-effects galore here.
   * @param {Note[]} local
   * @param {Note[]} remote
   */
  reconcileNotes(local, remote) {
    const result = reconcileAnnotations(
      local,
      remote,
      (n) => n.dateUpdated,
      (n) => this._noteSave(n),
      (n) => this._noteUpdate(n),
      (n) => this._noteDelete(n),
    );

    this.notes.clear();
    this.notes.add(...result.values());
    this.storageProvider?.storeNotes(this.bookId, this.listAllNotes());
  }
}

/**
 * Reconciles local and server annotations and updates the corresponding internal property.
 * @param {Number} bookId Book ID.
 * @param {Bookmark[] | Note[]} local  Annotations received from device storage
 * @param {Bookmark[] | Note[]} remote Annotations recevied from server
 * @param {Function} dateFn function for retrieving date used for comparisons
 * @param {Function} saveFn function to save the annotation via the API
 * @param {Function} updateFn function to update the annotation via the API
 * @param {Function} deleteFn function to delete an annotation via the API
 * @returns {Map<string, Bookmark | Note>} Returns a Map of annotations keyed by location
 */
export function reconcileAnnotations(
  local,
  remote,
  dateFn,
  saveFn,
  updateFn,
  deleteFn,
) {
  // Initialize a working set composed of the remote annotations.
  const workingSet = new Map(remote.map((a) => [a.location, a]));

  local.forEach((a) => {
    if (!a.synced && !a.shouldDelete && !workingSet.has(a.location)) {
      // Local annotation that has not been saved to the server.
      // Add it to the working set and queue up an API save.
      workingSet.set(a.location, a);
      saveFn(a);
    } else if (
      workingSet.has(a.location) &&
      dateFn(workingSet.get(a.location)) < dateFn(a)
    ) {
      // Local annotation appears to be newer than remote annotation for the same location.
      // Use the local annotation in the working set and issue an update for the remote.
      // Note that it doesn't matter if the local annotation is synced -- it's still for the
      // same location and is newer, so it "wins," and it's always an update because the
      // location is occupied in the remote set.
      workingSet.set(a.location, a);
      updateFn(a);
    } else if (
      a.synced &&
      a.shouldDelete &&
      dateFn(a) >= dateFn(workingSet.get(a.location))
    ) {
      // Server-synced annotation has been marked for deletion locally.
      // Keep it in memory but queue up a delete.
      // (If it was never synced, there's no remote item to delete. If its creation date
      // precedes the remote version's date, then the remote version is newer and "wins.")
      workingSet.set(a.location, a);
      deleteFn(a);
    }
  });

  // Return the working set
  return workingSet;
}
